import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { tags as tagsApi, projects as projectsApi } from '../lib/api.js'
import { groupTags } from '../components/tag-palette.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const tableHeader = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${colors.border};
`

const tableRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const parentRow = css`
  ${tableRow};
  font-weight: ${font.weight.medium};
`

const childRow = css`
  ${tableRow};
  font-size: ${font.size.sm};
`

const col = (flex) => css`flex: ${flex}; min-width: 0; overflow: hidden; text-overflow: ellipsis;`

const swatchCell = css`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
`

const nameCell = css`
  display: flex;
  align-items: center;
  min-width: 0;
`

const indentLevel = (depth) => depth > 0 ? css`
  margin-left: calc(${space.lg} * ${depth});
  padding-left: ${space.sm};
  border-left: 2px solid ${colors.border};
` : ''

const shortcutBadge = css`
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  padding: 1px 6px;
  font-size: ${font.size.xs};
  font-family: 'SF Mono', monospace;
  font-weight: ${font.weight.semibold};
  color: ${colors.textSecondary};
`

const smallBtn = css`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  &:hover { background: ${colors.bg}; }
`

const deleteBtn = css`
  ${smallBtn};
  color: ${colors.brand};
  &:hover { background: ${colors.brandLight}; }
`

// --- Form styles ---

const formOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const formCard = css`
  ${card};
  width: 480px;
  max-height: 90vh;
  overflow-y: auto;
`

const formTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.lg};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const colorRow = css`
  display: flex;
  gap: ${space.sm};
  flex-wrap: wrap;
`

const colorSwatch = css`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.1s ease;
`

const colorSwatchSelected = css`
  border-color: ${colors.text} !important;
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Preset colors ---
const PRESET_COLORS = [
  '#ff5c5c', '#ff6b8a', '#ffa94d', '#ffe66d',
  '#a9e34b', '#4ecdc4', '#74c0fc', '#c792ea',
  '#e8e0da', '#999999',
]

// --- Component ---

export function TagsView() {
  let projectId = null
  let project = null
  let tagList = []
  let loading = true
  let showForm = false
  let editingTag = null
  let formData = { name: '', description: '', color: PRESET_COLORS[0], shortcut: '', parentId: '' }
  let saving = false

  async function loadTags(pid) {
    loading = true
    try {
      tagList = await tagsApi.list(pid)
    } catch (err) {
      console.error('Failed to load tags:', err)
      tagList = []
    } finally {
      loading = false
      m.redraw()
    }
  }

  function openCreate() {
    editingTag = null
    formData = { name: '', description: '', color: PRESET_COLORS[0], shortcut: '', parentId: '' }
    showForm = true
  }

  function openEdit(tag) {
    editingTag = tag
    formData = {
      name: tag.name,
      description: tag.description || '',
      color: tag.color || PRESET_COLORS[0],
      shortcut: tag.shortcut || '',
      parentId: tag.parent_id || '',
    }
    showForm = true
  }

  function closeForm() {
    showForm = false
    editingTag = null
    saving = false
  }

  async function submitForm() {
    if (!formData.name.trim()) return
    saving = true
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color || undefined,
        shortcut: formData.shortcut.trim() || null,
        parentId: formData.parentId || null,
      }
      if (editingTag) {
        await tagsApi.update(projectId, editingTag.id, payload)
      } else {
        await tagsApi.create(projectId, payload)
      }
      closeForm()
      await loadTags(projectId)
    } catch (err) {
      console.error('Failed to save tag:', err)
      saving = false
      m.redraw()
    }
  }

  async function deleteTag(tag) {
    try {
      await tagsApi.delete(projectId, tag.id)
      await loadTags(projectId)
    } catch (err) {
      console.error('Failed to delete tag:', err)
    }
  }

  function renderTagRow(tag, depth) {
    return m('div', { key: tag.id, class: depth > 0 ? childRow : parentRow }, [
      m('div', { class: col(0.3) },
        m('div', {
          class: swatchCell,
          style: `background: ${tag.color || colors.border};`,
        }),
      ),
      m('div', { class: `${col(3)} ${nameCell}` },
        m('span', { class: indentLevel(depth) }, tag.name),
      ),
      m('div', { class: col(1) },
        tag.shortcut
          ? m('span', { class: shortcutBadge }, tag.shortcut.toUpperCase())
          : m('span', { style: `color: ${colors.textMuted};` }, '—'),
      ),
      m('div', { class: `${col(3)} ${css`font-size: ${font.size.sm}; color: ${colors.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`}` },
        tag.description || ''),
      m('div', { class: `${col(1.5)} ${css`display: flex; gap: ${space.xs}; justify-content: flex-end;`}` }, [
        m('button', { class: smallBtn, onclick: () => openEdit(tag) }, 'Edit'),
        m('button', { class: deleteBtn, onclick: () => deleteTag(tag) }, 'Delete'),
      ]),
    ])
  }

  function renderTagAndChildren(tag, depth, childrenByParent, rows) {
    rows.push(renderTagRow(tag, depth))
    const children = childrenByParent.get(tag.id)
    if (children) {
      for (const child of children) {
        renderTagAndChildren(child, depth + 1, childrenByParent, rows)
      }
    }
  }

  function renderTagTable() {
    const { parents, childrenByParent } = groupTags(tagList)
    const rows = []
    rows.push(m('div', { key: 'header', class: tableHeader }, [
      m('div', { class: col(0.3) }, ''),
      m('div', { class: col(3) }, 'Name'),
      m('div', { class: col(1) }, 'Shortcut'),
      m('div', { class: col(3) }, 'Description'),
      m('div', { class: col(1.5) }, ''),
    ]))
    for (const parent of parents) {
      renderTagAndChildren(parent, 0, childrenByParent, rows)
    }
    return rows
  }

  // Track which shortcuts are in use
  function usedShortcuts() {
    const used = new Set()
    for (const tag of tagList) {
      if (tag.shortcut && (!editingTag || tag.id !== editingTag.id)) {
        used.add(tag.shortcut.toLowerCase())
      }
    }
    return used
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadTags(projectId)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      const used = usedShortcuts()

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Tags',
        ]),

        m('div', {
          style: `display: flex; justify-content: space-between; align-items: center; margin-bottom: ${space.lg};`,
        }, [
          m('h1', { class: pageTitle, style: 'margin-bottom: 0;' }, 'Tags'),
          m('button', { class: btnPrimary, onclick: openCreate }, 'New tag'),
        ]),

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : tagList.length === 0
            ? m('div', { class: emptyState }, [
                m('p', 'No tags yet.'),
                m('p', { style: `margin-top: ${space.sm};` },
                  'Create tags to categorize and filter sessions.'),
              ])
            : m('div', { class: card }, renderTagTable()),

        // --- Create/Edit form modal ---
        showForm
          ? m('div', { class: formOverlay, onclick: (e) => { if (e.target === e.currentTarget) closeForm() } }, [
              m('div', { class: formCard }, [
                m('div', { class: formTitle },
                  editingTag ? 'Edit tag' : 'New tag'),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Name'),
                  m('input', {
                    class: input,
                    value: formData.name,
                    oninput: (e) => { formData.name = e.target.value },
                    placeholder: 'e.g., Housing, Safety, Off-topic',
                    autofocus: true,
                  }),
                ]),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Description'),
                  m('textarea', {
                    class: input,
                    style: 'min-height: 80px; resize: vertical;',
                    value: formData.description,
                    oninput: (e) => { formData.description = e.target.value },
                    placeholder: 'Detailed description (useful for LLM-assisted tagging later)',
                  }),
                ]),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Keyboard shortcut'),
                  m('input', {
                    class: input,
                    style: 'width: 60px; text-align: center; text-transform: uppercase;',
                    value: formData.shortcut,
                    maxlength: 1,
                    oninput: (e) => {
                      const val = e.target.value.slice(-1).toLowerCase()
                      formData.shortcut = val
                    },
                    placeholder: 'e.g., H',
                  }),
                  used.has(formData.shortcut.toLowerCase()) && formData.shortcut
                    ? m('div', {
                        style: `font-size: ${font.size.xs}; color: ${colors.brand}; margin-top: 2px;`,
                      }, 'This shortcut is already in use')
                    : null,
                ]),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Color'),
                  m('div', { class: colorRow },
                    PRESET_COLORS.map(c =>
                      m('div', {
                        key: c,
                        class: `${colorSwatch} ${formData.color === c ? colorSwatchSelected : ''}`,
                        style: `background: ${c};`,
                        onclick: () => { formData.color = c },
                      })
                    )
                  ),
                ]),

                tagList.length > 0
                  ? m('div', { class: formRow }, [
                      m('label', { class: label }, 'Parent tag (optional)'),
                      m('select', {
                        class: input,
                        value: formData.parentId,
                        onchange: (e) => { formData.parentId = e.target.value },
                      }, [
                        m('option', { value: '' }, 'None'),
                        ...tagList
                          .filter(t => !editingTag || t.id !== editingTag.id)
                          .map(t => m('option', { value: t.id }, t.name)),
                      ]),
                    ])
                  : null,

                m('div', { class: formActions }, [
                  m('button', { class: btnSecondary, onclick: closeForm }, 'Cancel'),
                  m('button', {
                    class: btnPrimary,
                    disabled: saving || !formData.name.trim(),
                    onclick: submitForm,
                  }, saving ? 'Saving...' : (editingTag ? 'Save' : 'Create')),
                ]),
              ]),
            ])
          : null,
      ])
    },
  }
}
